import { PaperClipOutlined } from '@ant-design/icons';
import { Alert, Table, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';

import {
    BackgroundCheckScreeningStatus,
    BackgroundCheckScreeningStatusDto,
    BackgroundCheckScreeningsDto,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import { HttpError } from '@clh/util';

import { useToast } from '../../hooks/use-toast';
import Spinner from '../../spinner';

import { screeningResultLabel, screeningStatusLabel } from './screening-status';

function StatusDot({ status }: { status: BackgroundCheckScreeningStatus }) {
    const { token } = theme.useToken();
    const colors: Record<BackgroundCheckScreeningStatus, string> = {
        NOT_STARTED: token.colorTextQuaternary,
        IN_PROGRESS: token.colorInfo,
        COMPLETE: token.colorSuccess,
        NEEDS_REVIEW: token.colorWarning,
        CANCELED: token.colorTextQuaternary,
    };
    const color = colors[status];

    return (
        <span
            style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                marginRight: 6,
                borderRadius: '50%',
                border: `1.5px solid ${color}`,
                background:
                    status === BackgroundCheckScreeningStatus.NotStarted
                        ? 'transparent'
                        : color,
            }}
        />
    );
}

function ScreeningReport({
    backgroundCheckId,
    screening: { caseDataId, name, result },
}: {
    backgroundCheckId: string;
    screening: BackgroundCheckScreeningStatusDto;
}) {
    const api = useApiClient();
    const { notification } = useToast();

    if (!api || !caseDataId || !result) {
        return <>—</>;
    }

    const reportUrl = async () => {
        const { url } =
            await api.backgroundCheckControllerGetScreeningReportUrl({
                backgroundCheckId,
                caseDataId,
            });
        return url;
    };

    const showError = (err: unknown) => {
        notification.error({
            message:
                err instanceof HttpError && err.statusCode === 422
                    ? 'Report not available yet.'
                    : "Couldn't open the report.",
        });
    };

    const view = async () => {
        const tab = window.open('', '_blank');
        if (!tab) {
            notification.error({
                message: 'Allow pop-ups to view the report.',
            });
            return;
        }
        try {
            const url = await reportUrl();
            tab.opener = null;
            tab.location.href = url;
        } catch (err) {
            tab.close();
            showError(err);
        }
    };

    const download = async () => {
        try {
            const pdf =
                await api.backgroundCheckControllerDownloadScreeningReport({
                    backgroundCheckId,
                    caseDataId,
                });
            const href = URL.createObjectURL(pdf);
            const link = document.createElement('a');
            link.href = href;
            link.download = `${name}.pdf`;
            link.click();
            URL.revokeObjectURL(href);
        } catch (err) {
            showError(err);
        }
    };

    return (
        <span style={{ whiteSpace: 'nowrap' }}>
            <PaperClipOutlined />{' '}
            <Typography.Link onClick={view}>View report</Typography.Link>
            {' · '}
            <Typography.Link onClick={download}>Download</Typography.Link>
        </span>
    );
}

function orderSummary({ orderedAt, screenings }: BackgroundCheckScreeningsDto) {
    const count = `${screenings.length} ${screenings.length === 1 ? 'item' : 'items'}`;

    return orderedAt
        ? `Ordered ${orderedAt.toLocaleDateString()} · ${count}`
        : count;
}

export function KarmaCheckScreenings({
    backgroundCheckId,
}: {
    backgroundCheckId: string;
}) {
    const api = useApiClient();
    const [order, setOrder] = useState<BackgroundCheckScreeningsDto>();
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        api?.backgroundCheckControllerListScreenings({ backgroundCheckId })
            .then(setOrder)
            .catch(() => setFailed(true));
    }, [api, backgroundCheckId]);

    if (failed) {
        return (
            <Alert
                type="error"
                showIcon
                message="Couldn't load screenings from KarmaCheck."
            />
        );
    }

    if (!order) {
        return <Spinner />;
    }

    return (
        <>
            <Typography.Text type="secondary">
                {orderSummary(order)}
            </Typography.Text>
            <Table<BackgroundCheckScreeningStatusDto>
                size="small"
                rowKey={(screening) =>
                    screening.caseDataId ?? screening.serviceId
                }
                pagination={false}
                dataSource={order.screenings}
                locale={{ emptyText: 'No screenings in this order.' }}
                style={{ marginTop: 8 }}
                columns={[
                    { title: 'Item', dataIndex: 'name' },
                    {
                        title: 'Status',
                        render: (_val, screening) => (
                            <span style={{ whiteSpace: 'nowrap' }}>
                                <StatusDot status={screening.status} />
                                {screeningStatusLabel(screening)}
                            </span>
                        ),
                    },
                    {
                        title: 'Updated',
                        dataIndex: 'updatedAt',
                        render: (updatedAt: Date | null) =>
                            updatedAt?.toLocaleDateString() ?? '—',
                    },
                    {
                        title: 'Result',
                        dataIndex: 'result',
                        render: screeningResultLabel,
                    },
                    {
                        title: 'Report',
                        render: (_val, screening) => (
                            <ScreeningReport
                                backgroundCheckId={backgroundCheckId}
                                screening={screening}
                            />
                        ),
                    },
                ]}
            />
        </>
    );
}
